// Vue

import { ref, Ref } from "vue";
// Dependencies
import { defineStore } from "pinia";

// Types
import { Material, LCIAResult } from "@/types/Views/MaterialDatabase/Material";
import { BuildingProjectType } from "@/features/BuildingProject";
import { StageDefaultEmissionsType } from "../types/StageDefaultEmissionsType";
import { editedSince } from "@/features/BuildingProject/services/editedSince";

// Localization
import { genericEpdBufferStages } from "@/providers/Localization";

// Error tracking
import * as Sentry from "@sentry/vue";

// GraphQL
import {
  GetStageDefaultEmissionQuery,
  GetProjectStandardValueStagesDocument,
  type GetProjectStandardValueStagesQuery,
  type GetProjectStandardValueStagesQueryVariables,
  InsertProjectStandardValueStagesDocument,
  type InsertProjectStandardValueStagesMutation,
  type InsertProjectStandardValueStagesMutationVariables,
  DeleteProjectStandardValueStagesDocument,
  type DeleteProjectStandardValueStagesMutation,
  type DeleteProjectStandardValueStagesMutationVariables,
  SaveProjectStandardValueStagesWithVersionDocument,
  type SaveProjectStandardValueStagesWithVersionMutation,
  type SaveProjectStandardValueStagesWithVersionMutationVariables,
} from "@/graphql";
import gaiupApolloClient from "@/graphql/services/ApolloClient";
import {
  isVersioningSchemaMissing,
  isVersionEntityTypeUnsupported,
} from "@/pages/ProjectVersionHistory/services/versioningSchema";

interface StageDefaultEmissionsProvider {
  defaultEmissions: Ref<StageDefaultEmissionsType>;
  enabledStages: Ref<Record<string, boolean>>;
  // Getters
  getStageDefaultEmissions: () => StageDefaultEmissionsType;
  getActiveDefaultEmissions: () => StageDefaultEmissionsType;
  getBuildingTypeFilteredEmissions: () => StageDefaultEmissionsType;

  // Methods
  mapData: (data: GetStageDefaultEmissionQuery) => void;
  getMaterialStages: (material: Material) => LCIAResult[];
  getMissingStages: ({ material, buildingProject }: { material: Material; buildingProject: BuildingProjectType }) => number[];
  useDefaultEmissions: () => boolean;
  getDefaultEmissionsFromMissingStages: (stages: number[]) => StageDefaultEmissionsType;
  getGenericEpdDefaultEmissionsBufferFromStages: (stages: number[]) => number[];
  isStageEnabled: (stageDefaultEmissionId: string) => boolean;
  setEnabledStages: (next: Record<string, boolean>, options?: { asSaved?: boolean }) => void;
  hasEnabledStageEdits: () => boolean;
  setBuildingTypeFilter: (buildingTypeId: number | null) => void;
  saveEnabledStages: (projectId: string) => Promise<void>;

  // Utility
  getLoading: () => boolean;
  setLoading: (newValue: boolean) => void;
}

const useStageDefaultEmissions = defineStore("StageDefaultEmissionsProvider", (): StageDefaultEmissionsProvider => {
  // Refs

  const defaultEmissions = ref<StageDefaultEmissionsType>([]);

  // Keyed by stageDefaultEmission uuid, not stage id — Denmark can have several standard value rows sharing one stage id.
  // A missing entry is treated as enabled (see isStageEnabled).
  const enabledStages = ref<Record<string, boolean>>({});

  // The enable map as the server last held it; null means "not read yet" (editedSince treats that as unedited).
  // Set only via setEnabledStages(next, { asSaved: true }) — that's what keeps it in sync with a real save.
  const savedEnabledStages = ref<string | null>(null);

  // Filters standard value rows by `stageDefaultEmissionBuildingTypes`; rows with none apply to every project.
  // `null` means no building type selected yet, so filtering is disabled and all rows pass.
  const buildingTypeFilter = ref<number | null>(null);

  // Getters

  function getStageDefaultEmissions() {
    return defaultEmissions.value;
  }

  function isStageEnabled(stageDefaultEmissionId: string) {
    return enabledStages.value[stageDefaultEmissionId] ?? true;
  }

  // Sorted before comparing: editedSince compares serialised JSON, and iteration order can differ between
  // two maps holding the same answers.
  const enabledStagesSignature = (map: Record<string, boolean>) =>
    JSON.stringify(Object.entries(map).sort(([left], [right]) => left.localeCompare(right)));

  // options.asSaved: true when `next` reflects the server (seed, reset, post-write) rather than a reader's edit.
  function setEnabledStages(next: Record<string, boolean>, options?: { asSaved?: boolean }) {
    enabledStages.value = { ...next };
    if (options?.asSaved) savedEnabledStages.value = enabledStagesSignature(next);
  }

  // Whether a standard value was toggled since it was read or last saved — these per-row toggles
  // don't live on the project like other settings sections' state, so edits need separate tracking.
  function hasEnabledStageEdits() {
    return editedSince(enabledStagesSignature(enabledStages.value), savedEnabledStages.value);
  }

  function setBuildingTypeFilter(buildingTypeId: number | null) {
    buildingTypeFilter.value = buildingTypeId;
  }

  function matchesBuildingType(emission: StageDefaultEmissionsType[number]) {
    const buildingTypes = emission.stageDefaultEmissionBuildingTypes ?? [];
    if (buildingTypes.length === 0) return true;
    if (buildingTypeFilter.value === null) return true;
    return buildingTypes.some((buildingType) => buildingType.buildingTypeId === buildingTypeFilter.value);
  }

  function getBuildingTypeFilteredEmissions() {
    return defaultEmissions.value.filter((emission) => matchesBuildingType(emission));
  }

  function getActiveDefaultEmissions() {
    return defaultEmissions.value.filter((emission) => matchesBuildingType(emission) && isStageEnabled(emission.id));
  }

  // Setters

  function setStageDefaultEmissions(newModels: StageDefaultEmissionsType) {
    defaultEmissions.value = newModels;
  }

  // Methods

  function mapData(data: GetStageDefaultEmissionQuery) {
    const { stageDefaultEmission } = data;

    // Map all models
    if (!stageDefaultEmission) return;
    setStageDefaultEmissions(
      stageDefaultEmission.map((x) => ({
        ...x,
        label: `${x.label} (${x.stage.label})`,
      }))
    );
  }

  function getMaterialStages(material: Material) {
    return material.lciaResults.filter(
      (result) =>
        result.moduleStatusEnum === 2 &&
        result.moduleTypeEnum !== 1 &&
        result.moduleTypeEnum !== 2 &&
        result.moduleTypeEnum !== 3 &&
        result.indicators.length > 0
    );
  }

  function getMissingStages({ material, buildingProject }: { material: Material; buildingProject: BuildingProjectType }) {
    // Return phases that exist on buildingProject but not on material
    return buildingProject.emissionCalculationAssumptions.emissionMetaData.includedPhases.filter(
      (stage) => !getMaterialStages(material).some((materialStage) => materialStage.moduleTypeEnum === stage)
    );
  }

  function useDefaultEmissions() {
    if (!defaultEmissions.value.length) return false;
    if (defaultEmissions.value[0].country === null) return false;
    // Either use the default emission from the country config or the generic EPD part stages
    return (
      defaultEmissions.value[0].countryConfig?.useStageDefaultEmission ||
      genericEpdBufferStages[defaultEmissions.value[0].country]?.length > 0
    );
  }

  function getDefaultEmissionsFromMissingStages(stages: number[]) {
    return defaultEmissions.value.filter(
      (emission) => stages.includes(emission.stage.id) && matchesBuildingType(emission) && isStageEnabled(emission.id)
    );
  }

  function getGenericEpdDefaultEmissionsBufferFromStages(stages: number[]) {
    if (!defaultEmissions.value.length) return [];
    // The generic-EPD buffer is an Iceland-only concept keyed by stage number, not a per-row standard value —
    // the Denmark enable map (keyed by uuid) doesn't apply here.
    return genericEpdBufferStages[defaultEmissions.value[0].country]?.filter((stage) => stages.includes(stage)) ?? [];
  }

  // Called once from the save flow (not per toggle) once projectId is known. The map is the source of truth:
  // `true` upserts, `false` deletes; an empty map (no Denmark rows, or step never opened) is a safe no-op.
  async function saveEnabledStages(projectId: string) {
    // Called on every exit this function takes WITHOUT throwing, including the no-op paths — never in the
    // catch: a failed write leaves real unsaved edits, and this marker is the only thing that says so.
    const markSaved = () => {
      savedEnabledStages.value = enabledStagesSignature(enabledStages.value);
    };

    const entries = Object.entries(enabledStages.value);
    if (entries.length === 0) {
      markSaved();
      return;
    }

    const enabledStageIds = entries.filter(([, isEnabled]) => isEnabled).map(([stageId]) => stageId);
    const disabledStageIds = entries.filter(([, isEnabled]) => !isEnabled).map(([stageId]) => stageId);

    try {
      const current = await gaiupApolloClient.query<
        GetProjectStandardValueStagesQuery,
        GetProjectStandardValueStagesQueryVariables
      >({
        query: GetProjectStandardValueStagesDocument,
        fetchPolicy: "network-only",
        variables: { projectId },
      });
      const persistedStageIds = new Set((current.data?.projectStandardValueStage ?? []).map((row) => row.stageId));
      const stagesToEnable = enabledStageIds.filter((stageId) => !persistedStageIds.has(stageId));
      const stagesToDisable = disabledStageIds.filter((stageId) => persistedStageIds.has(stageId));
      if (stagesToEnable.length === 0 && stagesToDisable.length === 0) {
        markSaved();
        return;
      }

      const stageLabel = (stageId: string) =>
        defaultEmissions.value.find((emission) => emission.id === stageId)?.label ?? "Standard value";
      const summaryParts: string[] = [];
      if (stagesToEnable.length > 0) summaryParts.push(`${stagesToEnable.length} enabled`);
      if (stagesToDisable.length > 0) summaryParts.push(`${stagesToDisable.length} disabled`);

      const variables: SaveProjectStandardValueStagesWithVersionMutationVariables = {
        objects: stagesToEnable.map((stageId) => ({ projectId, stageId })),
        projectId,
        stageIds: stagesToDisable,
        // entityType must stay "standard_value" (snake_case) — the literal value the check constraint expects.
        version: {
          projectId,
          entityType: "standard_value",
          operation: "update",
          summary: `Standard value stages updated (${summaryParts.join(", ")})`,
          changes: [
            ...stagesToEnable.map((stageId) => ({ field: stageLabel(stageId), from: "Disabled", to: "Enabled" })),
            ...stagesToDisable.map((stageId) => ({ field: stageLabel(stageId), from: "Enabled", to: "Disabled" })),
          ],
        },
      };

      try {
        await gaiupApolloClient.mutate<
          SaveProjectStandardValueStagesWithVersionMutation,
          SaveProjectStandardValueStagesWithVersionMutationVariables
        >({
          mutation: SaveProjectStandardValueStagesWithVersionDocument,
          variables,
        });
      } catch (mutationError) {
        if (!isVersioningSchemaMissing(mutationError) && !isVersionEntityTypeUnsupported(mutationError)) {
          throw mutationError;
        }
        await Promise.all([
          stagesToEnable.length > 0
            ? gaiupApolloClient.mutate<
                InsertProjectStandardValueStagesMutation,
                InsertProjectStandardValueStagesMutationVariables
              >({
                mutation: InsertProjectStandardValueStagesDocument,
                variables: { objects: stagesToEnable.map((stageId) => ({ projectId, stageId })) },
              })
            : undefined,
          stagesToDisable.length > 0
            ? gaiupApolloClient.mutate<
                DeleteProjectStandardValueStagesMutation,
                DeleteProjectStandardValueStagesMutationVariables
              >({
                mutation: DeleteProjectStandardValueStagesDocument,
                variables: { projectId, stageIds: stagesToDisable },
              })
            : undefined,
        ]);
      }

      markSaved();
    } catch (error) {
      // Don't let a standard-value persistence failure abort the whole project
      // save — surface it to Sentry instead.
      Sentry.captureException(error);
    }
  }

  // Utility

  const loading = ref<boolean>(false);

  function setLoading(newValue: boolean) {
    loading.value = newValue;
  }

  function getLoading() {
    return loading.value;
  }

  // Returns

  return {
    defaultEmissions,
    enabledStages,
    // Getters
    getStageDefaultEmissions,
    getActiveDefaultEmissions,
    getBuildingTypeFilteredEmissions,

    // Methods
    mapData,
    getMaterialStages,
    getMissingStages,
    useDefaultEmissions,
    getDefaultEmissionsFromMissingStages,
    getGenericEpdDefaultEmissionsBufferFromStages,
    isStageEnabled,
    setEnabledStages,
    hasEnabledStageEdits,
    setBuildingTypeFilter,
    saveEnabledStages,

    // Utility
    getLoading,
    setLoading,
  };
});

export default useStageDefaultEmissions;
